import {
	GridColDef,
	GridFilterModel,
	GridPaginationModel,
	GridSortModel,
	getGridDateOperators,
	getGridNumericOperators,
} from '@mui/x-data-grid-premium';
import { isEmptyGridFilterValue } from '@parkchirp/ui-library';
import React, { Dispatch, SetStateAction } from 'react';
import type { SetShowDialog, SetShowSnackbar } from '../../types';
import { convertMuiFilterForV2Api } from '../../utils';
import { ParkerDetailModal } from './parkerDetail';

export const OnSortModelChange =
	({
		setPaginationModel,
		setSortOrder,
	}: {
		setPaginationModel: Dispatch<SetStateAction<GridPaginationModel>>;
		setSortOrder: Dispatch<
			SetStateAction<{
				field: string;
				sort: string;
			}>
		>;
	}) =>
	(sortModel: GridSortModel) => {
		setPaginationModel((prev) => ({ ...prev, page: 0 }));
		if (!sortModel.length) {
			return setSortOrder({ field: 'createdAt', sort: 'asc' });
		}

		const { field, sort } = sortModel[0];

		return setSortOrder({ field, sort });
	};

export const OnFilterModelChange =
	({
		setPaginationModel,
		setFilters,
	}: {
		setPaginationModel: Dispatch<SetStateAction<GridPaginationModel>>;
		setFilters: Dispatch<
			SetStateAction<
				{
					propertyName: string;
					op: string;
					value: string;
				}[]
			>
		>;
	}) =>
	(filterModel: GridFilterModel) => {
		setPaginationModel((prev) => ({ ...prev, page: 0 }));

		const { items: filterItems } = filterModel;

		if (!filterItems.length || filterItems.every((i) => !i.value)) {
			setFilters([]);
			return;
		}

		const queryFilters = filterItems
			.map((filter) => {
				const { field, operator, value } = filter;

				if (isEmptyGridFilterValue(value) && !['isEmpty', 'isNotEmpty'].includes(operator)) return [];

				const op = convertMuiFilterForV2Api(operator, value);

				return {
					propertyName: field,
					op: op.op,
					value: value ?? null,
				};
			})
			.flat();

		setFilters(queryFilters);
	};

export function getParkerReservedSpacesTableColumns({
	setShowDialog,
	refetchAllParkerReservedSpacesFromCache,
	theme,
	setShowSnackbar,
}: {
	setShowDialog: SetShowDialog;
	refetchAllParkerReservedSpacesFromCache: () => void;
	theme: any;
	setShowSnackbar: SetShowSnackbar;
}): GridColDef[] {
	const style = {
		background: 'none',
		border: 0,
		color: 'blue',
		fontSize: 'inherit',
		cursor: 'pointer',
	};

	const numericOperators = getGridNumericOperators();
	const dateOperators = getGridDateOperators();

	const columnDefs: GridColDef[] = [
		{
			field: 'createdAt',
			headerName: 'Date Added',
			groupable: false,
			filterOperators: dateOperators,
		},
		{
			field: 'lastName',
			headerName: 'Last Name',
			groupable: false,
			renderCell: (params) => (
				<a
					style={style}
					onClick={() => {
						setShowDialog(
							<ParkerDetailModal
								theme={theme}
								handleClose={() => {
									refetchAllParkerReservedSpacesFromCache();
									setShowDialog(null);
								}}
								setShowSnackbar={setShowSnackbar}
								parkerId={params.row.parkerId}
								parkerFirstName={params.row.firstName}
								parkerLastName={params.row.lastName}
								parkerAccountId={params.row.accountId}
								parkerSubscriptionName={params.row.subscriptionName}
								facilityId={params.row.facilityId}
								subscriptionId={params.row.subscriptionId}
								parkerFacilityName={params.row.facilityName}
								parkerFacilityNumber={params.row.facilityNumber}
								refetchParkersFromCache={refetchAllParkerReservedSpacesFromCache}
							/>,
						);
					}}
					data-cy='last-name-link'
				>
					{params.row.lastName}
				</a>
			),
		},
		{
			field: 'firstName',
			headerName: 'First Name',
			groupable: false,
			renderCell: (params) => (
				<a
					style={style}
					onClick={() => {
						setShowDialog(
							<ParkerDetailModal
								theme={theme}
								handleClose={() => {
									refetchAllParkerReservedSpacesFromCache();
									setShowDialog(null);
								}}
								setShowSnackbar={setShowSnackbar}
								parkerId={params.row.parkerId}
								parkerFirstName={params.row.firstName}
								parkerLastName={params.row.lastName}
								parkerAccountId={params.row.accountId}
								parkerSubscriptionName={params.row.subscriptionName}
								facilityId={params.row.facilityId}
								subscriptionId={params.row.subscriptionId}
								parkerFacilityName={params.row.facilityName}
								parkerFacilityNumber={params.row.facilityNumber}
								refetchParkersFromCache={refetchAllParkerReservedSpacesFromCache}
							/>,
						);
					}}
					data-cy='first-name-link'
				>
					{params.row.firstName}
				</a>
			),
		},
		{
			field: 'active',
			headerName: 'Start Date',
			groupable: false,
			filterOperators: dateOperators,
		},
		{
			field: 'inactive',
			headerName: 'End Date',
			groupable: false,
			filterOperators: dateOperators,
		},
		{
			field: 'level',
			headerName: 'Level',
			groupable: false,
		},
		{
			field: 'spaceNumber',
			headerName: 'Space Number',
			groupable: false,
		},
		{
			field: 'facilityName',
			headerName: 'Facility Name',
			groupable: false,
		},
		{
			field: 'facilityNumber',
			headerName: 'Facility Number',
			groupable: false,
			filterOperators: numericOperators,
		},
		{
			field: 'subscriptionName',
			headerName: 'Subscription Name',
			groupable: false,
		},
		{
			field: 'accountId',
			headerName: 'Account ID',
			filterOperators: numericOperators,
			groupable: false,
			renderCell: (params) => `AC-${params.row.accountId}`,
		},
		{
			field: 'readableId',
			headerName: 'Sub. ID',
			filterOperators: numericOperators,
			groupable: false,
		},
		{
			field: 'payorType',
			headerName: 'Payor Type',
			groupable: false,
			type: 'singleSelect' as const,
			valueOptions: [
				{ value: 'Individual', label: 'Individual' },
				{ value: 'Business', label: 'Business' },
			],
		},
		{
			field: 'email',
			headerName: 'Email',
			groupable: false,
		},
	];
	return columnDefs.map((colDef) => ({
		...colDef,
		groupable: false,
	}));
}
